import React, { useEffect, useMemo, useRef, useState } from "react";
// ag-grid removed — using card list layout
import {
  CircularProgress,
  Dialog,
  LinearProgress,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import _ from "lodash";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  ButtonSize,
  HStack,
  IconButtonType,
  IconCTAButton,
  VStack,
} from "../../components/utils";
import SearchInput from "../../components/shared/SearchInput";
import AlertDialog from "../../components/shared/AlertDialog";
import { ReportNames } from "./Reports";
// removed ag-grid helpers
import ReportDialog from "./dialogs/ReportDialog";
import PoolStatementDialog from "./dialogs/PoolStatementDialog";
import SH6ReportDialog from "../esopOverview/SH6ReportDialog";
import FinancialReportDialog from "../esopOverview/FinancialReportDialog";
import GenericTableHeader from "../../shared/GenericTableHeader";
import { Input, Label } from "../../components/shared/InputField";
import {
  convertBase64ToBlob,
  downloadBlobObject,
  downloadFileFromURL,
  downloadS3File,
} from "../../utils/DownloadFile";
import {
  deleteHistoricReport,
  downloadSync,
  getReportInfo,
} from "../../api/Esop";
import { Select } from "../../components/shared/Select";
import { useGetScheduledReports } from "../../queries/scheduledReports";
import EsopAccountingDialog from "../esopOverview/EsopAccountingDialog";
import Section10CReportDialog from "../esopOverview/Section10CReport";
import ExpenseReportDialog from "../esopOverview/ExpenseReportDialog";
import { useCompanyStore } from "../../store";
import { useReportJobStore } from "../../store/reportJobStore";
import { useReportPiiMaskDefault } from "../../queries/siteSettings";
import { formatDisplayDate } from "../../utils/date";
import { BulkActions } from "../esopOverview/GrantsTable";
import { CTADropdown } from "../../components/shared/Dropdown";
import ScheduleManagementDialog from "./dialogs/ScheduleManagementDialog";
import { useGetAllCompanyConfigs, useGetHistoricReports } from "../../queries";

interface ReportItems extends ReportNames {
  path?: string;
  createdAt?: string;
  reportName?: string;
  status?: string;
  data?: any;
  error?: any;
}
const renderStatusCell = (params: any) => {
  const status = params?.value;
  const error = params?.data?.data ?? params?.data?.error;

  const baseClasses =
    "py-1 px-3 rounded text-xs flex items-center gap-2 justify-center flex-wrap h-8 w-24";

  const statusMap: Record<
    string,
    { label: string; bg: string; text: string; showSpinner?: boolean }
  > = {
    SUCCESS: { label: "Success", bg: "bg-green-100", text: "text-green-700" },
    IN_PROGRESS: {
      label: "In Progress",
      bg: "bg-blue-100",
      text: "text-blue-700",
    },
    FAILURE: { label: "Failed", bg: "bg-red-100", text: "text-red-700" },
    QUEUED: { label: "Queued", bg: "bg-gray-100", text: "text-gray-700" },
  };

  const cfg = statusMap[status] || {
    label: "-",
    bg: "bg-gray-100",
    text: "text-gray-700",
  };

  const badge = (
    <span
      className={`${baseClasses} ${cfg.bg} ${cfg.text} items-center self-start`.trim()}
    >
      {cfg.showSpinner && <CircularProgress size={14} />}
      <span>{cfg.label}</span>
    </span>
  );

  return <div className="w-full flex items-start justify-center">{badge}</div>;
};
// ─── Shared types ────────────────────────────────────────────────────────────

type ReportColumnInfo = { key: string; label: string; description?: string };
type ReportSheetInfo = { name: string; columns: ReportColumnInfo[] };
type ReportInfoEntry = {
  purpose: string;
  columns: ReportColumnInfo[];
  sheets?: ReportSheetInfo[];
};

// ─── Column popover ───────────────────────────────────────────────────────────

const POPOVER_WIDTH = 400;

function ReportColumnsPopover({
  info,
  reportName,
  pos,
  activeTab,
  onTabChange,
  onClose,
}: {
  info: ReportInfoEntry;
  reportName: string;
  pos: { top: number; left: number };
  activeTab: string;
  onTabChange: (name: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    // Close when the page scrolls, but not when the user scrolls inside the popover itself
    function handleScroll(e: Event) {
      if (ref.current && ref.current.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose]);

  const renderColList = (cols: ReportColumnInfo[]) => (
    <div className="divide-y divide-borderColor">
      {cols.map((col) => (
        <div
          key={col.key}
          className="flex items-start gap-3 px-4 py-2.5 hover:bg-tertiaryBg transition-colors"
        >
          <span className="text-[12px] font-medium text-primaryText min-w-0 flex-1 leading-snug">
            {col.label}
          </span>
          {col.description && col.description !== col.label && (
            <span className="text-[11px] text-descriptionColor text-right shrink-0 max-w-[52%] leading-snug">
              {col.description}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  const isMultiSheet = info.sheets && info.sheets.length > 1;
  const activeSheetData = isMultiSheet
    ? info.sheets!.find((s) => s.name === activeTab) ?? info.sheets![0]
    : null;

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: POPOVER_WIDTH,
        zIndex: 9999,
      }}
      className="bg-white border border-borderColor rounded-xl shadow-2xl overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-borderColor bg-tertiaryBg">
        <div>
          <p className="text-xs font-semibold text-headerColor">{reportName}</p>
          <p className="text-[10px] text-secondaryText mt-0.5">
            {isMultiSheet
              ? `${info.sheets!.length} sheets · ${
                  info.columns.length
                } total columns`
              : `${info.columns.length} columns`}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-200 transition-colors text-secondaryText"
        >
          <Icon icon="mdi:close" width={14} />
        </button>
      </div>

      {/* Sheet tabs for multi-sheet reports */}
      {isMultiSheet && (
        <div className="flex border-b border-borderColor">
          {info.sheets!.map((sheet) => {
            const isActive = activeTab === sheet.name;
            return (
              <button
                key={sheet.name}
                onClick={() => onTabChange(sheet.name)}
                className={`px-4 py-2 text-[11px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-secondaryText hover:text-primaryText"
                }`}
              >
                {sheet.name}
                <span className="ml-1.5 text-[10px] font-normal opacity-60">
                  {sheet.columns.length}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Column list — scrollable */}
      <div className="overflow-y-auto max-h-80">
        {/* Column header row */}
        <div className="flex items-center gap-3 px-4 py-1.5 bg-tertiaryBg border-b border-borderColor sticky top-0">
          <span className="text-[10px] font-semibold text-secondaryText uppercase tracking-wide flex-1">
            Column
          </span>
          <span className="text-[10px] font-semibold text-secondaryText uppercase tracking-wide text-right shrink-0 max-w-[52%]">
            What it means
          </span>
        </div>
        {isMultiSheet
          ? renderColList(activeSheetData!.columns)
          : renderColList(info.columns)}
      </div>
    </div>
  );
}

// ─── Main table ───────────────────────────────────────────────────────────────

const ReportsAgGridTable = ({
  reportsData,
}: {
  reportsData: ReportNames[];
}) => {
  const [dialog, setDialog] = useState<{
    open: boolean;
    data: any;
  }>({ open: false, data: {} });
  const [reportToDelete, setReportToDelete] = useState<{ id: string } | null>(
    null
  );
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const { reports } = useReportJobStore();
  const { companyData } = useCompanyStore();
  const [reportInfoMap, setReportInfoMap] = useState<
    Record<string, ReportInfoEntry>
  >({});
  // Popover state — only one report open at a time.
  const [openPopoverKey, setOpenPopoverKey] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Active sheet tab per report (rowKey → sheet name).
  const [activeSheetTabs, setActiveSheetTabs] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    getReportInfo()
      .then((data) => {
        const map: typeof reportInfoMap = {};
        data.forEach((r) => {
          map[r.reportType] = {
            purpose: r.purpose,
            columns: r.columns,
            sheets: r.sheets,
          };
        });
        setReportInfoMap(map);
      })
      .catch(() => {});
    // eslint-disable-line
  }, []);
  const {
    maskPiiData: shouldPiiBeMasked,
    setMaskPiiData: setShouldPiiBeMasked,
  } = useReportPiiMaskDefault();
  const { data: syncConfigs, refetch: refetchConfigs } =
    useGetAllCompanyConfigs();
  const { data: historicReports, refetch: refetchHistoricReports } =
    useGetHistoricReports();

  const confirmDeleteHistoricReport = async () => {
    if (!reportToDelete) return;
    setIsDeletingReport(true);
    try {
      await deleteHistoricReport(reportToDelete.id);
      toast.success("Generated report deleted");
      setReportToDelete(null);
      refetchHistoricReports();
    } catch {
      toast.error("Couldn't delete the generated report");
    } finally {
      setIsDeletingReport(false);
    }
  };
  const { data: scheduledReports = [], refetch: refetchScheduledReports } =
    useGetScheduledReports();
  const companyId = useMemo(() => companyData?.id || "", [companyData]);
  const isInitialCompanyMount = useRef(true);
  useEffect(() => {
    if (isInitialCompanyMount.current) {
      isInitialCompanyMount.current = false;
      return;
    }
    refetchConfigs();
    refetchHistoricReports();
    refetchScheduledReports();
  }, [companyId]);
  const reportStatus = useMemo(
    () => reports[companyId || ""] || {},
    [companyId, reports]
  );
  const availableReports = useMemo(() => {
    const reportsToReturn = [];
    if (syncConfigs && syncConfigs.length > 0) {
      reportsToReturn.push(...reportsData);
    } else {
      reportsToReturn.push(
        ...reportsData.filter(
          (data) => data.name !== "Employee Data Sync history"
        )
      );
    }
    if (historicReports && historicReports.length > 0 && companyData) {
      const currentReports = reportStatus;
      reportsToReturn.forEach((report: ReportItems) => {
        const historicReport = historicReports.find(
          (historic) => historic.reportType === report.reportType
        );
        const job = currentReports?.[report.reportType!];

        if (job) {
          report.data = job.data;
          report.error = job.error;
        }

        if (historicReport) {
          report.path = historicReport.path;
          report.createdAt = historicReport.createdAt;
          report.reportName = historicReport.reportName;
        }

        const jobUpdated = job?.updatedAt
          ? new Date(job.updatedAt).getTime()
          : undefined;
        const historicUpdated = historicReport?.statusMeta?.createdAt
          ? new Date(historicReport.statusMeta.createdAt).getTime()
          : undefined;

        if (
          job &&
          historicReport &&
          jobUpdated != null &&
          historicUpdated != null
        ) {
          report.status =
            jobUpdated >= historicUpdated ? job.status : historicReport.status;
        } else if (job) {
          report.status = job.status;
        } else if (historicReport) {
          report.status = historicReport.status;
        }
      });
    }
    return reportsToReturn;
  }, [syncConfigs, historicReports, reportStatus]);
  // ag-grid removed: local list view used instead
  function onGenerateButtonClicked(params: any) {
    const reportName = params?.data?.name;
    if (reportName === "Employee Data Sync history") {
      const earliestSync = _.minBy(syncConfigs, (config) => config.createdAt);
      const earliestSyncDate = earliestSync?.createdAt;
      if (earliestSyncDate) {
        const minDate = new Date(earliestSyncDate);
        minDate.setHours(0, 0, 0, 0);
        setDownloadDialogData({ ...downloadDialogData, minDate });
      }
    }
    setDialog({ open: true, data: params.data });
  }
  const generateButton = (data: ReportItems) => {
    const isProcessing =
      data.status === "IN_PROGRESS" || data.status === "QUEUED";
    return (
      <div className="flex flex-col items-end gap-1">
        <ButtonPrimary1
          onClick={() => onGenerateButtonClicked({ data })}
          disabled={isProcessing}
          className="px-4 py-2 rounded-md font-medium flex items-center gap-1.5"
        >
          {isProcessing ? (
            <>
              <CircularProgress size={12} className="text-primary" />
              Generating…
            </>
          ) : (
            <>
              <Icon icon="mdi:cog-play-outline" width={15} />
              Generate Report
            </>
          )}
        </ButtonPrimary1>
        {isProcessing && (
          <span className="text-[10px] text-gray-400 text-right">
            Report is being prepared — come back to download
          </span>
        )}
      </div>
    );
  };
  const actions = useMemo(
    () =>
      Array.from(
        new Set(
          syncConfigs?.map((config) => ({
            label: config.name || config.masterDataType,
            value: config.id,
          }))
        )
      ),
    [syncConfigs]
  );
  const [filteredRowData, setFilteredRowData] = useState<any>([]);

  useEffect(() => {
    if (availableReports !== undefined) {
      setFilteredRowData(availableReports);
    }
  }, [availableReports]);
  const [query, setQuery] = useState("");

  const filteredReports = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    const source =
      filteredRowData && filteredRowData.length
        ? filteredRowData
        : availableReports || [];
    if (!q) return source;
    return source.filter((r: ReportItems) => {
      const info = r.reportType ? reportInfoMap[r.reportType] : undefined;
      const colText = info?.columns.map((c) => c.label).join(" ") ?? "";
      const colDescriptions =
        info?.columns.map((c) => c.description).join(" ") ?? "";
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q) ||
        (info?.purpose ?? "").toLowerCase().includes(q) ||
        colText.toLowerCase().includes(q) ||
        colDescriptions?.toLowerCase()?.includes(q)
      );
    });
  }, [filteredRowData, availableReports, query]);
  const [downloadDialogData, setDownloadDialogData] = useState<{
    open: boolean;
    fromDate?: Date;
    toDate?: Date;
    minDate?: Date;
    datasource?: string;
    dataSourceId?: string;
  }>({
    open: false,
  });
  const [reportBeingDownloaded, setReportBeingDownloaded] = useState(false);
  const [showDatasourceError, setShowDatasourceError] = useState(false);
  const maxDate = new Date();
  const sortComparator = (
    valueA: any,
    valueB: any,
    nodeA: any,
    nodeB: any,
    isInverted: any
  ) => {
    if (valueA === valueB) return 0;
    return valueA > valueB ? 1 : -1;
  };
  const downloadSyncHistory = () => {
    if (!downloadDialogData.datasource) {
      setShowDatasourceError(true);
      return;
    }
    setShowDatasourceError(false);
    setReportBeingDownloaded(true);
    downloadSync({
      datasource: downloadDialogData.datasource,
      fromDate: downloadDialogData.fromDate,
      toDate: downloadDialogData.toDate,
      hidePiiData: shouldPiiBeMasked,
    })
      .then((data) => {
        convertBase64ToBlob(data?.data?.base64File, data?.data?.fileType).then(
          (blobObject) => {
            const fileName = `${companyData?.name || ""} sync histories.xlsx`;
            downloadBlobObject(blobObject, `${fileName}`);
            setReportBeingDownloaded(false);
            setDialog({ open: false, data: undefined });
            setDownloadDialogData({ open: false });
          }
        );
      })
      .catch((err) => {
        toast("Error while downloading", { type: "error" });
        setReportBeingDownloaded(false);
      });
  };

  const renderActionsCell = (params: any) => {
    const data = params?.data || {};
    const rowActions = [
      { name: "View recent reports" },
      { name: "Manage schedule" },
    ];
    return (
      <CTADropdown
        className="items-center bg-white align-middle"
        dropdownClassName=" xl:right-10 lg:right-8 md:right-2 bg-white z-10"
        actions={rowActions}
        onAction={(action) => {
          if (action.name === "Manage schedule") {
            setDialog({ open: true, data: { ...data, viewSchedule: true } });
          } else {
            setDialog({ open: true, data: { ...data, viewRecent: true } });
          }
        }}
        render={() => (
          <Icon
            icon="carbon:overflow-menu-horizontal"
            className="text-gray-400 rounded hover:bg-gray-300"
            height={20}
          />
        )}
      />
    );
  };

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const handleCloseDialog = () => {
    setDialog({ open: false, data: undefined });
    refetchHistoricReports();
    refetchScheduledReports();
  };
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const sortedReports = useMemo(() => {
    const reports = filteredReports || availableReports || [];
    return reports;
  }, [filteredReports, availableReports, scheduledReports]);

  return (
    <VStack className="w-full h-full">
      <GenericTableHeader
        heading={"Reports"}
        subHeading={""}
        customClassName="flex flex-col px-0 lg:justify-start"
        additionalInfo={<>{`${(filteredReports || []).length} reports`}</>}
      ></GenericTableHeader>
      <HStack className="items-center justify-between w-full gap-4 pt-4">
        <HStack className="items-center gap-4 ">
          <SearchInput
            className=""
            placeholder="Search reports..."
            onChange={(e: any) => setQuery(e.target.value)}
          />
        </HStack>
      </HStack>
      <VStack className="justify-start w-full overflow-none p-2 min-h-[400px]">
        <div className="overflow-x-auto w-full">
          <table className="table-auto w-full border-separate border-spacing-y-3">
            <thead className="sr-only">
              <tr>
                <th>Report</th>
                <th>Last generated</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="gap-4">
              {sortedReports.map((r: ReportItems, idx: number) => {
                const createdAt = r?.createdAt;
                const isProcessing =
                  r?.status === "IN_PROGRESS" || r?.status === "QUEUED";
                const lastGeneratedText = isProcessing
                  ? "Processing..."
                  : createdAt
                  ? `Last generated on ${formatDisplayDate(
                      new Date(createdAt)
                    )}`
                  : "Never generated";

                const rowKey = r.reportType || idx;

                const activeSchedule =
                  r.reportType &&
                  scheduledReports?.find(
                    (s) => s.reportType === r.reportType && s.isActive
                  );

                return (
                  <tr
                    key={rowKey}
                    className="w-full bg-white shadow-sm border border-gray-100 rounded-lg"
                  >
                    <td className="align-top w-1/2 p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-headerColor">
                          {r.name}
                        </span>
                        {activeSchedule && (
                          <span
                            title="Has active schedule"
                            className="inline-flex items-center gap-1 text-xxs font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5"
                          >
                            <Icon icon="mdi:clock-outline" width={10} />
                            Scheduled
                          </span>
                        )}
                      </div>

                      {/* Purpose */}
                      <p className="text-xs text-secondaryText mt-1 leading-relaxed">
                        {r.reportType && reportInfoMap[r.reportType]
                          ? reportInfoMap[r.reportType].purpose
                          : r.description}
                      </p>

                      {/* Column popover trigger */}
                      {r.reportType && reportInfoMap[r.reportType] && (
                        <button
                          onClick={(e) => {
                            const key = String(rowKey);
                            if (openPopoverKey === key) {
                              setOpenPopoverKey(null);
                              setPopoverPos(null);
                              return;
                            }
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            const left =
                              rect.left + POPOVER_WIDTH > window.innerWidth - 8
                                ? Math.max(
                                    8,
                                    window.innerWidth - POPOVER_WIDTH - 8
                                  )
                                : rect.left;
                            setOpenPopoverKey(key);
                            setPopoverPos({ top: rect.bottom + 6, left });
                          }}
                          className="mt-2 flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                        >
                          <Icon
                            icon={
                              openPopoverKey === String(rowKey)
                                ? "mdi:table-column-remove"
                                : "mdi:table-column"
                            }
                            width={13}
                          />
                          {(() => {
                            const info = reportInfoMap[r.reportType!];
                            if (info.sheets && info.sheets.length > 1) {
                              return info.sheets
                                .map((s) => `${s.name} (${s.columns.length})`)
                                .join(" · ");
                            }
                            return `${info.columns.length} columns in this report`;
                          })()}
                        </button>
                      )}
                    </td>

                    <td className="align-top w-1/5 p-4 ">
                      <div className="text-sm text-gray-700">
                        {lastGeneratedText}
                      </div>
                      {activeSchedule?.nextRunAt && (
                        <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                          <Icon
                            icon="mdi:clock-outline"
                            width={12}
                            className="shrink-0"
                          />
                          <span>
                            Next run:{" "}
                            {formatDisplayDate(
                              new Date(activeSchedule.nextRunAt)
                            )}
                          </span>
                        </div>
                      )}
                    </td>

                    <td className="align-top w-1/6 p-4 ">
                      <div className="flex justify-center">
                        {renderStatusCell({ value: r.status, data: r })}
                      </div>
                    </td>

                    <td className="align-top p-4 ">
                      <div className="flex items-start gap-3 justify-end">
                        {generateButton(r)}
                        <MuiTooltip
                          title={
                            r.path
                              ? `Download the last generated report${
                                  r.createdAt
                                    ? ` (${formatDisplayDate(r.createdAt)})`
                                    : ""
                                }`
                              : 'No report generated yet. Click "Generate Report" first and come back once it\'s ready.'
                          }
                          placement="top"
                          arrow
                        >
                          <span>
                            <button
                              disabled={!r.path}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                r.path
                                  ? "border-primary text-primary hover:bg-primary/10 cursor-pointer"
                                  : "border-gray-200 text-gray-300 cursor-not-allowed"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!r.path) return;
                                const fileName = `${companyData?.name || ""}-${
                                  r.name
                                }-${formatDisplayDate(r.createdAt)}.xlsx`;
                                downloadS3File(r.path, fileName, false);
                              }}
                            >
                              <Icon
                                icon="material-symbols:download"
                                width={16}
                              />
                              Download
                            </button>
                          </span>
                        </MuiTooltip>

                        {renderActionsCell({ data: r })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </VStack>
      <Dialog
        open={dialog.open}
        onClose={() => handleCloseDialog()}
        maxWidth="lg"
      >
        {dialog.data?.viewSchedule ? (
          <div className="w-[600px] mx-auto bg-white rounded-lg">
            <ScheduleManagementDialog
              reportType={dialog.data?.reportType}
              reportName={dialog.data?.name}
              onClose={() => setDialog({ open: false, data: undefined })}
            />
          </div>
        ) : dialog.data?.viewRecent ? (
          <div className="w-[680px] mx-auto bg-white rounded-lg">
            <div className="flex items-center justify-between px-6 text-lg font-medium border-b py-5">
              <div>Recent Reports for {dialog.data?.name}</div>
              <div
                onClick={() => setDialog({ open: false, data: undefined })}
                className="cursor-pointer text-gray-500"
              >
                X
              </div>
            </div>

            <VStack className="p-6 gap-y-3 overflow-y-auto max-h-[300px]">
              {(() => {
                const hist = historicReports?.find(
                  (r: any) => r.reportType === dialog.data?.reportType
                );
                const prevReports = hist?.previousReports || [];
                if (prevReports.length === 0) {
                  return (
                    <div className="text-sm text-gray-600">
                      No recent reports found
                    </div>
                  );
                }
                return prevReports
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((h: any, idx: number) => {
                    const date = new Date(h.createdAt);
                    const time = date.toLocaleTimeString([], {
                      hour12: true,
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center shadow-sm">
                            <Icon
                              icon="ic:round-description"
                              className="text-orange-501"
                              width={20}
                            />
                          </div>
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-800">
                              {formatDisplayDate(date)}
                            </div>
                            <div className="text-xxs text-gray-500">
                              Generated at {time}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {h.path ? (
                            <IconCTAButton
                              buttonType={IconButtonType.OUTLINED}
                              iconOnly
                              className="border-primary hover:bg-primary/20 focus:outline-none"
                              iconName="material-symbols:download"
                              iconClassName="text-primary"
                              buttonSize={ButtonSize.SMALL}
                              onClick={(e) => {
                                downloadS3File(
                                  h.path,
                                  `${companyData?.name || ""}-${
                                    dialog.data?.name
                                  }-${formatDisplayDate(h.createdAt)}.xlsx`,
                                  false
                                );
                                e.stopPropagation();
                              }}
                            />
                          ) : (
                            <div className="text-sm text-gray-400">—</div>
                          )}
                          <IconCTAButton
                            buttonType={IconButtonType.OUTLINED}
                            iconOnly
                            className="border-primary hover:bg-primary/20 focus:outline-none"
                            iconName="material-symbols:delete-outline"
                            iconClassName="text-red-600"
                            buttonSize={ButtonSize.SMALL}
                            onClick={(event) => {
                              event.stopPropagation();
                              setReportToDelete({ id: h.id });
                            }}
                          />
                        </div>
                      </div>
                    );
                  });
              })()}
            </VStack>
          </div>
        ) : dialog.data?.name === "Expense Report" ? (
          <div className="mx-auto bg-white rounded-lg min-h-min">
            <ExpenseReportDialog
              data={dialog.data}
              onClose={() => handleCloseDialog()}
            />
          </div>
        ) : dialog.data?.name === "Section 10-C Report" ? (
          <div className="w-[800px] mx-auto bg-white rounded-lg  min-h-min">
            <Section10CReportDialog
              data={dialog.data}
              onClose={() => handleCloseDialog()}
            />
          </div>
        ) : dialog.data?.name === "Pool Statement" ? (
          <div className="w-[800px] mx-auto bg-white rounded-lg  min-h-min">
            <PoolStatementDialog
              data={dialog.data}
              onClose={() => handleCloseDialog()}
            />
          </div>
        ) : dialog.data?.name === "ESOP Accounting Report" ? (
          <div className="mx-auto bg-white rounded-lg min-h-min">
            <EsopAccountingDialog onClose={() => handleCloseDialog()} />
          </div>
        ) : dialog.data?.name === "SH-6 Report" ? (
          <div className="mx-auto bg-white rounded-lg min-w-min min-h-min">
            <SH6ReportDialog onClose={() => handleCloseDialog()} />
          </div>
        ) : dialog.data?.name === "Employee Data Sync history" ? (
          <div className="w-[800px] mx-auto bg-white rounded-lg">
            <div className="justify-between px-10 text-lg font-medium border-b py-7">
              <h6 className="flex justify-between">
                Download Sync Details
                <span
                  onClick={() => handleCloseDialog()}
                  className="cursor-pointer"
                >
                  X
                </span>
              </h6>
            </div>
            <VStack aria-label="header" className="p-4 gap-y-2">
              <HStack className="mt-4 ml-8 gap-x-8">
                <VStack className="w-1/3">
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    onChange={(e) =>
                      setDownloadDialogData({
                        ...downloadDialogData,
                        fromDate: new Date(e.target.value),
                      })
                    }
                    min={
                      downloadDialogData?.minDate?.toISOString().split("T")[0]
                    }
                    max={maxDate?.toISOString().split("T")[0]}
                  />
                  {downloadDialogData && downloadDialogData.fromDate && (
                    <>
                      {downloadDialogData.minDate &&
                        downloadDialogData.fromDate.valueOf() <
                          (downloadDialogData.minDate?.valueOf() || 0) && (
                          <p className="text-red-500 text-xxs">
                            From date cannot be lesser than
                            {downloadDialogData.minDate?.toDateString()}
                          </p>
                        )}
                      {downloadDialogData.fromDate.valueOf() >
                        (maxDate?.valueOf() || Infinity) && (
                        <p className="text-red-500 text-xxs">
                          From date cannot be greater than
                          {maxDate?.toDateString()}
                        </p>
                      )}
                    </>
                  )}
                </VStack>
                <VStack className="w-1/3">
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    onChange={(e) =>
                      setDownloadDialogData({
                        ...downloadDialogData,
                        toDate: new Date(e.target.value),
                      })
                    }
                    min={
                      downloadDialogData?.minDate?.toISOString().split("T")[0]
                    }
                    max={maxDate?.toISOString().split("T")[0]}
                  />
                  {downloadDialogData && downloadDialogData.toDate && (
                    <>
                      {downloadDialogData.toDate.valueOf() <
                        (downloadDialogData.minDate?.valueOf() || 0) && (
                        <p className="text-red-500 text-xxs">
                          To date cannot be lesser than
                          {downloadDialogData.minDate?.toDateString()}
                        </p>
                      )}
                      {downloadDialogData.toDate.valueOf() >
                        (maxDate?.valueOf() || Infinity) && (
                        <p className="text-red-500 text-xxs">
                          To date cannot be greater than
                          {maxDate?.toDateString()}
                        </p>
                      )}
                      {downloadDialogData.fromDate &&
                        downloadDialogData.toDate &&
                        downloadDialogData.toDate.valueOf() <
                          downloadDialogData.fromDate.valueOf() && (
                          <p className="text-red-500 text-xxs">
                            To date cannot be lesser than from date:{" "}
                            {downloadDialogData.fromDate.toDateString()}
                          </p>
                        )}
                    </>
                  )}
                </VStack>
                <VStack className="w-1/3">
                  <Label>Datasource</Label>
                  <Select
                    options={actions}
                    textGetter={(e) => e.label}
                    valueGetter={(e) => e.value}
                    onChange={(e) => {
                      setShowDatasourceError(false);
                      setDownloadDialogData({
                        ...downloadDialogData,
                        datasource: e.target.value,
                      });
                    }}
                  />
                  {showDatasourceError && (
                    <p className="text-red-500 text-xs mt-1">
                      Datasource is required field
                    </p>
                  )}
                </VStack>
              </HStack>
              <HStack className="justify-end gap-2 mt-7">
                <input
                  type="checkbox"
                  checked={shouldPiiBeMasked}
                  onChange={() => setShouldPiiBeMasked(!shouldPiiBeMasked)}
                  className="text-white accent-orange-501"
                  name=""
                  id=""
                />
                <span>Hide all Personally Identifiable Information</span>
              </HStack>
              <HStack className="justify-end m-3">
                <p className="italic">Note: The date filters are inclusive </p>
              </HStack>
              <HStack className="justify-end gap-4">
                <ButtonSecondary>Cancel</ButtonSecondary>
                <ButtonPrimary
                  loading={reportBeingDownloaded}
                  onClick={downloadSyncHistory}
                >
                  Download
                </ButtonPrimary>
              </HStack>
            </VStack>
          </div>
        ) : (
          <div className="w-[800px] mx-auto bg-white rounded-lg  min-h-min">
            <ReportDialog
              data={dialog.data}
              onClose={() => handleCloseDialog()}
            />
          </div>
        )}
      </Dialog>

      <AlertDialog
        open={Boolean(reportToDelete)}
        message="Delete this generated report? It will no longer be available for download."
        primaryActionText="Delete report"
        secondaryActionText="Cancel"
        loading={isDeletingReport}
        onPrimaryAction={confirmDeleteHistoricReport}
        onSecondaryAction={() => setReportToDelete(null)}
      />

      {openPopoverKey !== null &&
        popoverPos &&
        (() => {
          // Find the report matching the open key
          const report = sortedReports.find(
            (r: ReportItems, i: number) =>
              String(r.reportType || i) === openPopoverKey
          ) as ReportItems | undefined;
          if (!report?.reportType) return null;
          const info = reportInfoMap[report.reportType];
          if (!info) return null;
          const activeTab =
            activeSheetTabs[openPopoverKey] ?? info.sheets?.[0]?.name ?? "";
          return (
            <ReportColumnsPopover
              key={openPopoverKey}
              info={info}
              reportName={report.name}
              pos={popoverPos}
              activeTab={activeTab}
              onTabChange={(name) =>
                setActiveSheetTabs((prev) => ({
                  ...prev,
                  [openPopoverKey]: name,
                }))
              }
              onClose={() => {
                setOpenPopoverKey(null);
                setPopoverPos(null);
              }}
            />
          );
        })()}
    </VStack>
  );
};

export default ReportsAgGridTable;
